import React, { useRef, useState } from 'react';
import { usePreferencesState } from '../hooks/usePreferencesState';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useWhatsAppConfig } from '../hooks/useWhatsAppConfig';
import BusinessInfoCard from '../components/preferences/BusinessInfoCard';
import GoogleCalendarCard from '../components/preferences/GoogleCalendarCard';
import CalendarSettingsCard from '../components/preferences/CalendarSettingsCard';
import BusinessDetailsCard from '../components/preferences/BusinessDetailsCard';
import WhatsAppConfigCard from '../components/preferences/WhatsAppConfigCard';
import HandoffConfigCard from '../components/preferences/HandoffConfigCard';
import PushNotificationCard from '../components/PushNotificationCard';
import Toast from '../components/Toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { UserSettings } from '../types/index';

type PreferencesTab = 'info' | 'appointments' | 'whatsapp';

const PREFERENCE_TABS: Array<{ id: PreferencesTab; label: string }> = [
  { id: 'info', label: 'Información' },
  { id: 'appointments', label: 'Citas' },
  { id: 'whatsapp', label: 'WhatsApp' },
];

const Preferences: React.FC = () => {
  const [activeTab, setActiveTab] = useState<PreferencesTab>('info');
  const handoffFlushRef = useRef<(() => UserSettings) | null>(null);
  const {
    preferences,
    setPreferences,
    loading,
    error,
    successMessage,
    handleSubmit,
    setError,
    setSuccessMessage,
    waTokenSaveNonce,
  } = usePreferencesState();

  const { googleCalendars, showReauthButton, handleReauthenticate } = useGoogleCalendar();

  const { webhookUrl, verifyToken, handleCopyToClipboard } = useWhatsAppConfig();

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-background p-6 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const handleUpdate = (newPreferences: typeof preferences) => {
    setPreferences(newPreferences);
  };

  const onFormSubmit = (e: React.FormEvent) => {
    const flushedPreferences = handoffFlushRef.current?.() ?? preferences;
    handleSubmit(e, flushedPreferences);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background p-6">
      {successMessage && (
        <Toast message={successMessage} type="success" onClose={() => setSuccessMessage(null)} />
      )}

      {error && <Toast message={error} type="error" onClose={() => setError(null)} />}

      {showReauthButton && (
        <div className="max-w-3xl mx-auto mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex flex-col items-center justify-center">
            <p className="text-yellow-800 mb-3">
              La autenticación con Google ha expirado. Necesitas volver a iniciar sesión para
              acceder a tus calendarios.
            </p>
            <button
              onClick={handleReauthenticate}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              Iniciar sesión con Google
            </button>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-primary mb-6">Preferencias</h1>

        <form onSubmit={onFormSubmit}>
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white/70 p-1.5 shadow-sm">
            <div className="grid grid-cols-3 gap-1" role="tablist" aria-label="Preferencias">
              {PREFERENCE_TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`preferences-panel-${tab.id}`}
                    id={`preferences-tab-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={`rounded-xl px-3 py-2.5 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      isActive
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white hover:text-primary'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            id="preferences-panel-info"
            role="tabpanel"
            aria-labelledby="preferences-tab-info"
            hidden={activeTab !== 'info'}
          >
            <BusinessInfoCard preferences={preferences} onUpdate={handleUpdate} />
            <BusinessDetailsCard preferences={preferences} onUpdate={handleUpdate} />
            <div className="mb-6">
              <PushNotificationCard />
            </div>
          </div>

          <div
            id="preferences-panel-appointments"
            role="tabpanel"
            aria-labelledby="preferences-tab-appointments"
            hidden={activeTab !== 'appointments'}
          >
            <GoogleCalendarCard
              preferences={preferences}
              googleCalendars={googleCalendars}
              onUpdate={handleUpdate}
            />
            <CalendarSettingsCard preferences={preferences} onUpdate={handleUpdate} />
          </div>

          <div
            id="preferences-panel-whatsapp"
            role="tabpanel"
            aria-labelledby="preferences-tab-whatsapp"
            hidden={activeTab !== 'whatsapp'}
          >
            <WhatsAppConfigCard
              preferences={preferences}
              webhookUrl={webhookUrl}
              verifyToken={verifyToken}
              tokenSaveNonce={waTokenSaveNonce}
              onCopy={handleCopyToClipboard}
              onUpdate={handleUpdate}
            />
            <HandoffConfigCard
              preferences={preferences}
              onUpdate={handleUpdate}
              flushRef={handoffFlushRef}
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Guardar Preferencias
          </button>
        </form>
      </div>
    </div>
  );
};

export default Preferences;
